import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncContent } from '~/components/AsyncContent';
import { UnitSelect } from '~/components/UnitSelect';
import { RESULTS_SUMMARY } from '~/data/surveys';

import { DonutChart } from '../ResultsFields';
import { SurveyResultsData, SurveySummaryData } from '../types';

import { RenderChart } from './RenderChart';
import { useSxStyles } from './useStyles';
import { useSurveyResults } from './useSurveyResults';

type Props = {
  resultId: string;
  handleResultsClose: () => void;
};

export const SurveyResultsDialog: React.FC<Props> = ({
  resultId,
  handleResultsClose,
}) => {
  const pdfContentRef = useRef<HTMLDivElement | null>(null);
  const {
    open,
    onDialogClose,
    surveyResults: resultsObj,
    menuList,
    selectValue,
    selectOnChange,
    isLoading,
    failedToLoad,
    exportResults,
    exportToPdf,
  } = useSurveyResults(resultId, handleResultsClose, pdfContentRef);

  const { summary, results, name: surveyName, showResults } = resultsObj;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const FilterSelect =
    menuList && menuList.length > 1 ? (
      <UnitSelect
        selectOnChange={selectOnChange}
        selectDefaultValue={-1}
        menuList={menuList}
      />
    ) : (
      <Typography color="primary" variant="subtitle1">
        {menuList[0].label}
      </Typography>
    );
  return (
    <Dialog
      open={open}
      onClose={onDialogClose}
      fullScreen
      color="primary"
      scroll="body"
    >
      <Box sx={sxStyles.root}>
        <DialogTitle>
          <Box sx={sxStyles.headerContainer}>
            <Typography variant="h5" sx={sxStyles.title}>
              {surveyName}
            </Typography>
            <Box sx={sxStyles.headerActions}>
              <AsyncContent isLoading={isLoading} failedToLoad={failedToLoad}>
                {FilterSelect}
              </AsyncContent>
              <Button variant="contained" onClick={exportToPdf}>
                {t('laExpPDF')}
              </Button>
              <Button variant="contained" onClick={exportResults}>
                {t('laExportToExcel')}
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent ref={pdfContentRef} sx={sxStyles.content}>
          <Grid container wrap="wrap" spacing={2}>
            {showResults ? (
              <>
                <Grid
                  container
                  item
                  direction="row"
                  wrap="wrap"
                  justifyContent="flex-start"
                  spacing={2}
                >
                  <Typography variant="h6">{t('title_summary')}</Typography>
                  <Grid
                    container
                    item
                    direction="row"
                    wrap="wrap"
                    justifyContent="flex-start"
                    spacing={2}
                  >
                    {summary
                      ? summary.map((s: SurveySummaryData, index: number) => (
                          <Grid item key={`${s.title}-${index}`}>
                            <DonutChart
                              data={s.data}
                              title={s.title}
                              percentageLabel={s.type !== RESULTS_SUMMARY.AGE}
                              total={s.total}
                            />
                          </Grid>
                        ))
                      : null}
                  </Grid>
                </Grid>

                <Grid
                  container
                  item
                  direction="row"
                  wrap="wrap"
                  justifyContent="flex-start"
                  spacing={2}
                >
                  <Typography variant="h6">
                    {t('title_results_by_question')}
                  </Typography>
                  <Grid
                    container
                    item
                    direction="row"
                    wrap="wrap"
                    justifyContent="flex-start"
                    spacing={2}
                  >
                    {results
                      ? results.map((r: SurveyResultsData, index: number) => (
                          <RenderChart
                            data={r}
                            key={`${r.title}-${r.id}-${index}`}
                          />
                        ))
                      : null}
                  </Grid>
                </Grid>
              </>
            ) : (
              <Grid
                container
                item
                direction="row"
                wrap="wrap"
                justifyContent="flex-start"
                spacing={2}
              >
                {
                  <Typography variant="h6">
                    {selectValue === -1
                      ? t('text_no_survey_responses')
                      : t('text_no_survey_responses_from_unit')}
                  </Typography>
                }
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            component="a"
            color="primary"
            onClick={onDialogClose}
          >
            {t('laClose')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
